import React, { useContext, useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { Eye, Filter, AArrowDown, AArrowUp } from "lucide-react";
import { getAllUser, updateUserProfile } from "../../reducers/authentication.slice";
import { convertDateToCT, countActiveFiltersAndSort } from "../../utils/util";
import { AuthContext } from "../../context/authContext";
import { getUsers } from "../../utils/getUser";
import { Button } from "../../components/ui/button";
import DataTable from "../../components/shared/DataTable";
import PageHeader from "../../components/shared/PageHeader";
import SearchBar from "../../components/shared/SearchBar";
import RefreshButton from "../../components/shared/RefreshButton";
import StatusBadge from "../../components/shared/StatusBadge";
import useMediaQuery from "../../components/shared/useMediaQuery";
import UserRegisterationEditForm from "./EditUserManagementForm";
import UserRegisterationForm from "./UserRegisterationForm";
import UserViewPage from "./UserViewProfile";
import ApiKeysTab from "./ApiKeysTab";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  DropdownMenuCheckboxItem,
  DropdownMenuItem,
} from "../../components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../../components/ui/dialog";

const USER_STATUSES = ["SUSPENDED", "ACTIVE", "BLOCKED"];

const statStyle = (accent) => ({
  background: "linear-gradient(180deg, rgba(255,255,255,0.025), transparent)",
  border: "1px solid rgba(255,255,255,0.06)",
  borderTop: `3px solid ${accent}`,
  borderRadius: 14,
  padding: "16px 18px",
});

const statLabelStyle = {
  display: "block",
  color: "#c3c8cc",
  fontSize: 10.5,
  fontWeight: 800,
  letterSpacing: "0.09em",
};

const statValueStyle = {
  display: "block",
  color: "#fff",
  fontFamily: "ui-monospace, SF Mono, Menlo, monospace",
  fontSize: 24,
  fontWeight: 800,
  marginTop: 8,
};

const UserManagementPage = () => {
  const user = getUsers();
  const [activeTab, setActiveTab] = useState("users");
  const [userDetails, setUserDetails] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [isRefresh, setIsRefresh] = useState(false);
  const [selectedStatuses, setSelectedStatuses] = useState(() => {
    const s = sessionStorage.getItem("userManagement_selectedStatuses");
    return s ? JSON.parse(s) : [];
  });
  const [sortDirection, setSortDirection] = useState(() => sessionStorage.getItem("userManagement_sortDirection") || null);
  const [searchTerm, setSearchTerm] = useState("");
  const [searchfilteredData, setSearchFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [viewUser, setViewUser] = useState(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [editUser, setEditUser] = useState(null);

  const dispatch = useDispatch();
  const { showToast } = useContext(AuthContext);
  const isMobile = useMediaQuery("(max-width: 640px)");

  const getAllManagementUser = () => {
    setShowLoader(true);
    dispatch(getAllUser()).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) {
        setShowLoader(false);
        showToast(res?.error?.message, "error");
        return;
      }
      setShowLoader(false);
      const formatted = res?.payload?.map((item) => ({
        dataOfJoining: item?.date_of_joining ? convertDateToCT(item.date_of_joining, "MMMM D, YYYY hh:mm:ss A") : undefined,
        fullName: item?.first_name + " " + item?.last_name,
        ...item,
      }));
      setUserDetails(formatted);
    });
  };

  useEffect(() => { getAllManagementUser(); }, [isRefresh]);

  const applyFilter = () => {
    let filtered = [...userDetails];
    if (selectedStatuses.length > 0) filtered = filtered.filter((u) => selectedStatuses.includes(u?.status));
    if (sortDirection === "ascending") filtered.sort((a, b) => a?.first_name?.localeCompare(b?.first_name));
    else if (sortDirection === "descending") filtered.sort((a, b) => b?.first_name?.localeCompare(a?.first_name));
    if (searchTerm.trim()) {
      const t = searchTerm.toLowerCase();
      filtered = filtered.filter(
        (u) =>
          (u.fullName && u.fullName.toLowerCase().includes(t)) ||
          (u.discord_id && u.discord_id.toLowerCase().includes(t))
      );
    }
    setSearchFilteredData(filtered);
    setCurrentPage(0);
  };

  useEffect(() => {
    sessionStorage.setItem("userManagement_selectedStatuses", JSON.stringify(selectedStatuses));
  }, [selectedStatuses]);

  useEffect(() => {
    if (sortDirection) sessionStorage.setItem("userManagement_sortDirection", sortDirection);
    else sessionStorage.removeItem("userManagement_sortDirection");
  }, [sortDirection]);

  useEffect(() => { applyFilter(); }, [userDetails, selectedStatuses, sortDirection, searchTerm]);

  const handleVisitProfile = (row) => {
    setViewUser(row);
  };

  const handleSuspendUser = (row) => {
    setShowLoader(true);
    dispatch(updateUserProfile({ status: "SUSPENDED", id: row?.id })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) { showToast(res?.error?.message, "error"); return; }
      setShowLoader(false);
      setIsRefresh((p) => !p);
      showToast("User is suspended", "success");
    });
  };

  const handleActiveUser = (row) => {
    setShowLoader(true);
    dispatch(updateUserProfile({ status: "ACTIVE", id: row?.id })).then((res) => {
      if (res?.error && Object.keys(res?.error)?.length > 0) { setShowLoader(false); showToast(res?.error?.message, "error"); return; }
      setIsRefresh((p) => !p);
      setShowLoader(false);
      showToast("User is Activated", "success");
    });
  };

  const paginatedRows = searchfilteredData.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);
  const activeFilterCount = countActiveFiltersAndSort(sortDirection, selectedStatuses);

  const userStats = useMemo(() => ({
    active: userDetails.filter((d) => d.status === "ACTIVE").length,
    suspended: userDetails.filter((d) => d.status === "SUSPENDED").length,
    blocked: userDetails.filter((d) => d.status === "BLOCKED").length,
  }), [userDetails]);

  const columns = [
    { key: "first_name", label: "First Name", width: 110 },
    { key: "last_name", label: "Last Name", width: 110 },
    {
      key: "discord_id",
      label: "Discord Username",
      width: 140,
      render: (row) => <span style={{ fontSize: 14 }}>{row.discord_id || "-"}</span>,
    },
    {
      key: "discord_role",
      label: "Discord Role",
      width: 110,
      render: (row) =>
        row.discord_role ? (
          <span
            style={{
              padding: "4px 8px",
              background: "#16ff163d",
              borderRadius: 5,
              fontSize: 14,
            }}
          >
            {row.discord_role[0]}
          </span>
        ) : (
          <span style={{ fontSize: 14 }}>-</span>
        ),
    },
    { key: "email", label: "Email", width: 170 },
    {
      key: "is_verified",
      label: "Is Verified",
      width: 100,
      align: "center",
      render: (row) => <span style={{ fontSize: 12 }}>{row?.is_verified ? "Verified" : "Not Verified"}</span>,
    },
    {
      key: "is_plaid_verified",
      label: "Plaid Verified",
      width: 110,
      align: "center",
      render: (row) => <span style={{ fontSize: 12 }}>{row?.is_plaid_verified ? "Verified" : "Not Verified"}</span>,
    },
    { key: "role", label: "Role", width: 90 },
    {
      key: "access_level",
      label: "Access Level",
      width: 100,
      align: "center",
      render: (row) => (
        <span
          style={{
            padding: "4px 8px",
            background:
              (row?.access_level || "WRITE") === "READ" ? "#4da3ff2e" : "#16ff163d",
            borderRadius: 5,
            fontSize: 14,
          }}
        >
          {row?.access_level || "WRITE"}
        </span>
      ),
    },
    { key: "login_type", label: "Login Type", width: 100 },
    { key: "ip_address", label: "IP Address", width: 110 },
    {
      key: "total_orders",
      label: "Total Orders",
      width: 100,
      align: "center",
      render: (row) => <span style={{ fontSize: 14 }}>{row.total_orders} order</span>,
    },
    {
      key: "total_payments",
      label: "Total Payments",
      width: 110,
      align: "right",
      render: (row) => <span style={{ fontSize: 14 }}>$ {row.total_payments}</span>,
    },
    { key: "dataOfJoining", label: "Date of Joining", width: 160 },
    {
      key: "status",
      label: "Status",
      width: 100,
      align: "center",
      render: (row) => <StatusBadge status={row?.status} />,
    },
    {
      key: "action",
      label: "Action",
      width: 130,
      align: "center",
      render: (row) => (
        <div style={{ display: "flex", gap: 6, alignItems: "center", justifyContent: "center" }}>
          {row?.status === "ACTIVE" ? (
            <Button
              size="sm"
              variant="destructive"
              style={{ fontSize: "0.85rem", fontWeight: 400 }}
              onClick={() => handleSuspendUser(row)}
            >
              Suspend
            </Button>
          ) : (
            <Button
              size="sm"
              style={{ background: "#4caf50", color: "#fff", fontSize: "0.85rem", fontWeight: 400 }}
              onClick={() => handleActiveUser(row)}
            >
              Activate
            </Button>
          )}
          <Button variant="ghost" size="icon-sm" onClick={() => handleVisitProfile(row)} title="View Details">
            <Eye size={15} />
          </Button>
        </div>
      ),
    },
  ];

  return (
    <div style={{ maxWidth: 1280, margin: "0 auto", width: "100%" }}>
      <div style={{ marginBottom: 20 }}>
        <PageHeader title="User/Member Management" subtitle="Manage all registered users on the platform." />
      </div>

      <div style={{ display: "flex", gap: 8, marginBottom: 20, overflowX: "auto", flexWrap: "nowrap", scrollbarWidth: "none", msOverflowStyle: "none" }}>
        {[
          { key: "users", label: "Users" },
          ...(user?.role === "SUPERADMIN" ? [{ key: "api-keys", label: "API Keys" }] : []),
        ].map((tab) => (
          <Button
            key={tab.key}
            type="button"
            variant={activeTab === tab.key ? "default" : "outline"}
            size="sm"
            onClick={() => setActiveTab(tab.key)}
            style={{ borderRadius: 999, color: activeTab === tab.key ? "#000" : "#c3c8cc", flexShrink: 0 }}
          >
            {tab.label}
          </Button>
        ))}
      </div>

      {activeTab === "api-keys" && user?.role === "SUPERADMIN" ? (
        <ApiKeysTab />
      ) : (
      <>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: `repeat(auto-fit, minmax(${isMobile ? "130px" : "160px"}, 1fr))`,
          gap: isMobile ? 10 : 14,
          marginBottom: 20,
        }}
      >
        <div style={statStyle("#4caf50")}>
          <span style={statLabelStyle}>ACTIVE</span>
          <span style={statValueStyle}>{userStats.active}</span>
        </div>
        <div style={statStyle("#e8b84b")}>
          <span style={statLabelStyle}>SUSPENDED</span>
          <span style={statValueStyle}>{userStats.suspended}</span>
        </div>
        <div style={statStyle("#f27059")}>
          <span style={statLabelStyle}>BLOCKED</span>
          <span style={statValueStyle}>{userStats.blocked}</span>
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: isMobile ? "stretch" : "center",
            gap: isMobile ? 10 : 12,
            flexWrap: "wrap",
            width: isMobile ? "100%" : "auto",
          }}
        >
          {!isMobile && <RefreshButton onRefresh={getAllManagementUser} spinning={showLoader} />}
          <div style={{ flex: isMobile ? 1 : "none" }}>
            <SearchBar placeholder="Search by name or Discord…" searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
          </div>
          <DropdownMenu onOpenChange={(open) => { if (!open) applyFilter(); }}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="default" style={{ width: isMobile ? "100%" : "auto" }}>
                <Filter size={14} /> Filter & Sort
                {activeFilterCount > 0 && (
                  <span
                    style={{
                      marginLeft: 4,
                      display: "inline-flex",
                      alignItems: "center",
                      justifyContent: "center",
                      minWidth: 16,
                      height: 16,
                      borderRadius: 8,
                      background: "#00ADB5",
                      color: "#000",
                      fontSize: 10,
                      fontWeight: 800,
                      padding: "0 4px",
                    }}
                  >
                    {activeFilterCount}
                  </span>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" style={{ minWidth: 220 }}>
              <DropdownMenuLabel>Filter by Status</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {USER_STATUSES.map((status) => (
                <DropdownMenuCheckboxItem
                  key={status}
                  checked={selectedStatuses.includes(status)}
                  onSelect={(e) => e.preventDefault()}
                  onCheckedChange={(checked) =>
                    setSelectedStatuses((prev) => (checked ? [...prev, status] : prev.filter((s) => s !== status)))
                  }
                >
                  {status}
                </DropdownMenuCheckboxItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuLabel>Sort by Name</DropdownMenuLabel>
              <DropdownMenuItem active={sortDirection === "ascending"} onClick={() => setSortDirection((prev) => prev === "ascending" ? null : "ascending")}>
                <AArrowUp size={14} style={{ marginRight: 6 }} /> Name: A to Z
              </DropdownMenuItem>
              <DropdownMenuItem active={sortDirection === "descending"} onClick={() => setSortDirection((prev) => prev === "descending" ? null : "descending")}>
                <AArrowDown size={14} style={{ marginRight: 6 }} /> Name: Z to A
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {/* Only superadmins can invite users */}
          {user?.role === "SUPERADMIN" && (
            <Button
              style={{ width: isMobile ? "100%" : "auto" }}
              onClick={() => setCreateOpen(true)}
            >
              Add New User
            </Button>
          )}
        </div>
      </div>

      <DataTable
        columns={columns}
        rows={paginatedRows}
        totalRows={searchfilteredData.length}
        currentPage={currentPage}
        rowsPerPage={rowsPerPage}
        onPageChange={(n) => setCurrentPage(n)}
        onRowsPerPageChange={(n) => { setRowsPerPage(n); setCurrentPage(0); }}
        loading={showLoader}
        minWidth={1400}
      />
      <Dialog open={Boolean(viewUser)} onOpenChange={(open) => !open && setViewUser(null)}>
        <DialogContent style={{ maxWidth: 680, width: isMobile ? "calc(100vw - 16px)" : "90vw", maxHeight: "92vh" }}>
          <DialogHeader>
            <DialogTitle>User Profile</DialogTitle>
            <DialogDescription>View managed user details, payment verification, and permissions.</DialogDescription>
          </DialogHeader>
          {viewUser && (
            <UserViewPage
              dialogMode
              userId={viewUser.id}
              onUpdated={getAllManagementUser}
              onEdit={(userToEdit) => {
                setViewUser(null);
                setEditUser(userToEdit);
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={createOpen} onOpenChange={(open) => !open && setCreateOpen(false)}>
        <DialogContent style={{ maxWidth: 560, width: isMobile ? "calc(100vw - 16px)" : "90vw", maxHeight: "92vh" }}>
          <DialogHeader>
            <DialogTitle>Add New User</DialogTitle>
            <DialogDescription>Create a new manager account and assign permissions.</DialogDescription>
          </DialogHeader>
          {createOpen && (
            <UserRegisterationForm
              dialogMode
              onSuccess={() => {
                setCreateOpen(false);
                getAllManagementUser();
              }}
            />
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={Boolean(editUser)} onOpenChange={(open) => !open && setEditUser(null)}>
        <DialogContent style={{ maxWidth: 560, width: isMobile ? "calc(100vw - 16px)" : "90vw", maxHeight: "92vh" }}>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Update access level and manager permissions.</DialogDescription>
          </DialogHeader>
          {editUser && (
            <UserRegisterationEditForm
              dialogMode
              userDetails={editUser}
              onCancel={() => setEditUser(null)}
              onSuccess={() => {
                setEditUser(null);
                getAllManagementUser();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
      </>
      )}
    </div>
  );
};

export default UserManagementPage;
